import Vue from 'vue';
import handleErrors from '@/lib/handleErrors';

function optionNamesByKey(options) {
  const names = {};
  if (options) {
    options.forEach((option) => {
      names[option.key] = option.name;
    });
  }

  return names;
}

// By default, we will allow for localized fields to fallback to the default
// English version if the French data isn't present. However, for NRCan, we
// only want this behavior for US stations. For NRCan, when using French then
// Canadian stations should never fallback to English even if the French data
// is missing.
let { localeDisableFallbackCountries } = window.afdcStationsOptions;
if (localeDisableFallbackCountries === undefined) {
  localeDisableFallbackCountries = [];
}

// If the localeDetect setting is set to "html-lang", then try to detect
// the locale from the document's <html> tag.
let overrideLocale;
let overrideLocaleLang;
let overrideLocaleCountry;
if (window.afdcStationsOptions && window.afdcStationsOptions.localeDetect === 'html-lang') {
  const { lang } = document.documentElement;
  if (lang) {
    // Detect the base language (eg, "en").
    overrideLocaleLang = lang.toLowerCase().substring(0, 2);

    // Append the country code (eg, "-CA").
    if (window.afdcStationsOptions.localeCountry) {
      overrideLocaleCountry = window.afdcStationsOptions.localeCountry;
      overrideLocale = `${overrideLocaleLang}-${overrideLocaleCountry}`;
    } else {
      overrideLocale = overrideLocaleLang;
    }
  }
}

const initialLocaleLang = overrideLocaleLang || 'en';
const initialLocaleCountry = overrideLocaleCountry || window.afdcStationsOptions.localeCountry || 'US';
const initialLocale = overrideLocale || `${initialLocaleLang}-${initialLocaleCountry}`;

const moduleState = {
  initialLocale,
  initialLocaleLang,
  initialLocaleCountry,
  locale: initialLocale,
  localeLang: initialLocaleLang,
  localeCountry: initialLocaleCountry,
  localeDisableFallbackCountries,
  data: {
    access_types: [],
    cng_fill_types_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    cng_psis_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    cng_vehicle_classes: [],
    countries: [],
    distance_units: [],
    electric_connectors_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    electric_levels_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    electric_networks_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    federal_agencies: [],
    fuels: [],
    lng_vehicle_classes: [],
    owner_types_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    payment_methods_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    regions_by_country: {
      CA: [],
      US: [],
      all: [],
    },
    status_types: [],
  },
  loaderError: null,
};

const moduleGetters = {
  getLoaderError: (state) => state.loaderError,
  initialLocale: (state) => state.initialLocale,
  initialLocaleLang: (state) => state.initialLocaleLang,
  initialLocaleCountry: (state) => state.initialLocaleCountry,
  locale: (state) => state.locale,
  localeLang: (state) => state.localeLang,
  localeCountry: (state) => state.localeCountry,
  localeDisableFallbackCountries: (state) => state.localeDisableFallbackCountries,
  loaderData: (state) => state.data,
  loaderFuels: (state) => state.data.fuels,
  loaderFuelNamesByKey: (state) => optionNamesByKey(state.data.fuels),
  loaderAccessTypes: (state) => state.data.access_types,
  loaderAccessTypeNamesByKey: (state) => optionNamesByKey(state.data.access_types),
  loaderStatusTypes: (state) => state.data.status_types,
  loaderStatusTypeNamesByKey: (state) => optionNamesByKey(state.data.status_types),
  loaderCngFillTypes: (state, getters) => state.data.cng_fill_types_by_country[getters.getCountry],
  loaderCngFillTypesByKey: (state) => optionNamesByKey(state.data.cng_fill_types_by_country.all),
  loaderCngVehicleClasses: (state) => state.data.cng_vehicle_classes,
  loaderCngVehicleClassesByKey: (state) => optionNamesByKey(state.data.cng_vehicle_classes),
  loaderCngPsis: (state, getters) => state.data.cng_psis_by_country[getters.getCountry],
  loaderCngPsisByKey: (state) => optionNamesByKey(state.data.cng_psis_by_country.all),
  loaderElecLevels: (state, getters) => state.data.electric_levels_by_country[getters.getCountry],
  loaderElecLevelsByKey: (state) => optionNamesByKey(state.data.electric_levels_by_country.all),
  loaderElecConnectors: (state, getters) => state.data.electric_connectors_by_country[getters.getCountry],
  loaderElecConnectorsByKey: (state) => optionNamesByKey(state.data.electric_connectors_by_country.all),
  loaderLngVehicleClasses: (state) => state.data.lng_vehicle_classes,
  loaderLngVehicleClassesByKey: (state) => optionNamesByKey(state.data.lng_vehicle_classes),
  loaderOwnerTypes: (state, getters) => state.data.owner_types_by_country[getters.getCountry],
  loaderOwnerTypeNamesByKey: (state) => optionNamesByKey(state.data.owner_types_by_country.all),
  loaderPaymentMethods: (state, getters) => state.data.payment_methods_by_country[getters.getCountry],
  loaderPaymentMethodNamesByKey: (state) => optionNamesByKey(state.data.payment_methods_by_country.all),
  loaderCountryNamesByKey: (state) => optionNamesByKey(state.data.countries),
  loaderCountries: (state) => state.data.countries,
  loaderRegions: (state, getters) => state.data.regions_by_country[getters.getCountry],
  loaderRegionNamesByKey: (state) => optionNamesByKey(state.data.regions_by_country.all),
  loaderDistanceUnits: (state) => state.data.distance_units,
  loaderHydrogenStandards: (state) => state.data.hy_standards,
  loaderHydrogenStandardNamesByKey: (state) => optionNamesByKey(state.data.hy_standards),
  loaderHydrogenPressure: (state) => state.data.hy_pressure,
  loaderHydrogenPressureNamesByKey: (state) => optionNamesByKey(state.data.hy_pressure),
  loaderEthanolBlends: (state) => state.data.ethanol_blends,
  loaderEthanolBlendNamesByKey: (state) => optionNamesByKey(state.data.ethanol_blends),
  loaderRenewableSources: (state) => state.data.renewable_sources,
  loaderRenewableSourceNamesByKey: (state) => optionNamesByKey(state.data.renewable_sources),
  postManufacturer: (state) => state.data.post_manufacturer,
  postManufacturerNamesByKey: (state) => optionNamesByKey(state.data.post_manufacturer),
  loaderLpgNozzleTypes: (state) => state.data.lpg_nozzle_types,
  loaderLpgNozzleTypeNamesByKey: (state) => optionNamesByKey(state.data.lpg_nozzle_types),
  loaderElecNetworks: (state, getters) => state.data.electric_networks_by_country[getters.getCountry],
  loaderElecNetworksByKey: (state) => optionNamesByKey(state.data.electric_networks_by_country.all),
};

const mutations = {
  saveLoaderData(state, data) {
    state.data = data;
  },

  setLocale(state, locale) {
    if (locale) {
      state.localeLang = locale.substring(0, 2).toLowerCase() || initialLocaleLang;
      state.localeCountry = locale.substring(3).toUpperCase() || initialLocaleCountry;
      state.locale = locale;
    }
  },

  setLoaderError(state, status) {
    state.loaderError = status;
  },
};

const actions = {
  async fetchLoaderData({ commit, getters }) {
    try {
      commit('setLoadingInitialLoader', true);

      const apiQuery = {
        api_key: window.afdcStationsOptions.apiKey,
        override_locale: overrideLocale,
      };
      const country = getters.getCountry;
      if (country) {
        apiQuery.country = country;
      }

      const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL_ROOT}/v1/locator/loader.json`, {
        params: apiQuery,
      });
      commit('saveLoaderData', response.data);
      commit('setLoaderError', null);
    } catch (e) {
      commit('setLoaderError', handleErrors(e));
    } finally {
      commit('setLoadingInitialLoader', false);
    }
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
